import React, { createContext, useCallback, useContext, useState } from 'react';

export interface ItemCarrinho {
  quadraId: string;
  quadraNome: string;
  localId: string;
  localNome: string;
  dataAgendamento: string;
  horaInicio: string;
  horaFim: string;
  valor?: number;
  valorOriginal?: number;
}

interface CarrinhoContextType {
  itens: ItemCarrinho[];
  localId: string | null;
  localNome: string | null;
  adicionarItem: (item: ItemCarrinho) => string | null; // retorna erro se local diferente
  removerItem: (quadraId: string, horaInicio: string) => void;
  limpar: () => void;
}

const CarrinhoContext = createContext<CarrinhoContextType>({
  itens: [],
  localId: null,
  localNome: null,
  adicionarItem: () => null,
  removerItem: () => {},
  limpar: () => {},
});

export const CarrinhoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [itens, setItens] = useState<ItemCarrinho[]>([]);

  const localId = itens[0]?.localId ?? null;
  const localNome = itens[0]?.localNome ?? null;

  const adicionarItem = useCallback((item: ItemCarrinho): string | null => {
    if (itens.length > 0 && itens[0].localId !== item.localId) {
      return `Todos os horários devem ser do mesmo local (${itens[0].localNome}). Limpe o carrinho para reservar em outro local.`;
    }
    const jaExiste = itens.some(
      i => i.quadraId === item.quadraId && i.horaInicio === item.horaInicio
    );
    if (!jaExiste) setItens(prev => [...prev, item]);
    return null;
  }, [itens]);

  const removerItem = useCallback((quadraId: string, horaInicio: string) => {
    setItens(prev => prev.filter(i => !(i.quadraId === quadraId && i.horaInicio === horaInicio)));
  }, []);

  const limpar = useCallback(() => setItens([]), []);

  return (
    <CarrinhoContext.Provider value={{ itens, localId, localNome, adicionarItem, removerItem, limpar }}>
      {children}
    </CarrinhoContext.Provider>
  );
};

export const useCarrinho = () => useContext(CarrinhoContext);
